import React, { useState } from 'react';
import PlaidLink from '../../../components/shared/PlaidLink';
import { 
  CreditCardIcon, 
  HomeIcon, 
  ArrowRightIcon, 
  BanknotesIcon, 
  BriefcaseIcon,
  BuildingLibraryIcon
} from '@heroicons/react/24/outline';

/**
 * Component that offers users options for connecting accounts
 * Includes All Accounts (recommended) and specific categories
 */
const PlaidConnectionOptions = ({ onSuccess, onExit, reconnectItemId = null }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  
  // If reconnecting, skip options and go directly to PlaidLink
  if (reconnectItemId) {
    return (
      <div className="p-3 sm:p-6 bg-white rounded-xl shadow-lg border border-gray-200 max-w-md mx-auto">
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-800">
          Reconnect Account
        </h3>
        <PlaidLink 
          connectionType="aggressive_all_accounts"
          buttonText="Reconnect Account"
          onSuccess={onSuccess}
          onExit={(err, metadata) => {
            if (onExit) onExit(err, metadata);
          }}
          reconnectItemId={reconnectItemId}
        />
      </div>
    );
  }
  
  // If an option is selected, show the PlaidLink for that option
  if (selectedOption) {
    const connectionTypes = {
      'all': 'aggressive_all_accounts',
      'banking': 'banking',
      'credit_cards': 'credit_cards',
      'loans': 'liabilities',
      'investments': 'investments'
    };
    
    const connectionTitles = {
      'all': 'Connect All Accounts',
      'banking': 'Connect Checking & Savings',
      'credit_cards': 'Connect Credit Cards',
      'loans': 'Connect Loans & Mortgages',
      'investments': 'Connect Investment Accounts'
    };
    
    return (
      <div className="p-3 sm:p-6 bg-white rounded-xl shadow-lg border border-gray-200 max-w-md mx-auto">
        <button 
          onClick={() => setSelectedOption(null)} 
          className="mb-3 sm:mb-4 text-indigo-600 flex items-center text-sm font-medium hover:text-indigo-800 transition-colors min-h-[44px]"
        >
          <ArrowRightIcon className="h-4 w-4 rotate-180 mr-1" />
          Back to options
        </button>
        
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-800">
          {connectionTitles[selectedOption]}
        </h3>
        
        <PlaidLink 
          connectionType={connectionTypes[selectedOption]}
          buttonText={connectionTitles[selectedOption]}
          onSuccess={onSuccess}
          onExit={(err, metadata) => {
            if (err) {
              // On error, go back to selection
              setSelectedOption(null);
            }
            if (onExit) onExit(err, metadata);
          }}
        />
      </div>
    );
  }
  
  // Otherwise, show the options
  return (
    <div className="p-3 sm:p-6 bg-white rounded-xl shadow-lg border border-gray-200 max-w-md mx-auto">
      <h2 className="text-base sm:text-xl font-semibold mb-2 sm:mb-3 text-gray-800">Choose which accounts to connect</h2>
      <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
        Select the type of financial accounts you'd like to add:
      </p>
      
      <div className="flex flex-col space-y-3 sm:space-y-4">
        {/* All Accounts Option (recommended) */}
        <button 
          onClick={() => setSelectedOption('all')}
          className="p-3 sm:p-5 border border-gray-200 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 text-left flex flex-col shadow-sm hover:shadow-md transform hover:-translate-y-1 min-h-[44px]"
        >
          <div className="flex items-center mb-2 sm:mb-3">
            <div className="p-2 sm:p-3 bg-indigo-100 rounded-lg mr-2 sm:mr-3">
              <BuildingLibraryIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600" />
            </div>
            <h3 className="font-semibold text-sm sm:text-lg text-gray-800">All Accounts</h3>
            <span className="ml-2 text-[10px] sm:text-xs text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">Recommended</span>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4 pl-9 sm:pl-12">
            Connect all available accounts in one step (checking, savings, credit cards, and loans).
          </p>
        </button>

        {/* Banking Option */}
        <button 
          onClick={() => setSelectedOption('banking')}
          className="p-3 sm:p-5 border border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-left flex flex-col shadow-sm hover:shadow-md transform hover:-translate-y-1 min-h-[44px]"
        >
          <div className="flex items-center mb-2 sm:mb-3">
            <div className="p-2 sm:p-3 bg-blue-100 rounded-lg mr-2 sm:mr-3">
              <BanknotesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-sm sm:text-lg text-gray-800">Checking & Savings</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4 pl-9 sm:pl-12">
            Connect checking and savings accounts to track daily finances.
          </p>
          <div className="mt-auto flex flex-wrap gap-1 sm:gap-2 pl-9 sm:pl-12">
            <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Checking
            </span>
            <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Savings
            </span>
          </div>
        </button>
        
        {/* Credit Cards Option */}
        <button 
          onClick={() => setSelectedOption('credit_cards')}
          className="p-3 sm:p-5 border border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all duration-200 text-left flex flex-col shadow-sm hover:shadow-md transform hover:-translate-y-1 min-h-[44px]"
        >
          <div className="flex items-center mb-2 sm:mb-3">
            <div className="p-2 sm:p-3 bg-purple-100 rounded-lg mr-2 sm:mr-3">
              <CreditCardIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-sm sm:text-lg text-gray-800">Credit Cards</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4 pl-9 sm:pl-12">
            Connect credit cards to track spending and manage payments.
          </p>
          <div className="mt-auto flex flex-wrap gap-1 sm:gap-2 pl-9 sm:pl-12">
            <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              Credit Cards
            </span>
            <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
              Store Cards
            </span>
          </div>
        </button>

        {/* Loans & Mortgages Option */}
        <button 
          onClick={() => setSelectedOption('loans')}
          className="p-3 sm:p-5 border border-gray-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all duration-200 text-left flex flex-col shadow-sm hover:shadow-md transform hover:-translate-y-1 min-h-[44px]"
        >
          <div className="flex items-center mb-2 sm:mb-3">
            <div className="p-2 sm:p-3 bg-orange-100 rounded-lg mr-2 sm:mr-3">
              <HomeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
            </div>
            <h3 className="font-semibold text-sm sm:text-lg text-gray-800">Loans & Mortgages</h3>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4 pl-9 sm:pl-12">
            Connect mortgage and loan accounts to track debt balances.
          </p>
          <div className="mt-auto flex flex-wrap gap-1 sm:gap-2 pl-9 sm:pl-12">
            <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              Mortgages
            </span>
            <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Auto Loans
            </span>
          </div>
        </button>

        {/* Investments Option */}
        <button 
          onClick={() => setSelectedOption('investments')}
          className="p-3 sm:p-5 border border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all duration-200 text-left flex flex-col shadow-sm hover:shadow-md transform hover:-translate-y-1 min-h-[44px]"
        >
          <div className="flex items-center mb-2 sm:mb-3">
            <div className="p-2 sm:p-3 bg-emerald-100 rounded-lg mr-2 sm:mr-3">
              <BriefcaseIcon className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-sm sm:text-lg text-gray-800">Investment Accounts</h3>
            <span className="ml-2 text-[10px] sm:text-xs text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Test Mode</span>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4 pl-9 sm:pl-12">
            Connect investment and retirement accounts to track your portfolio.
          </p>
          <div className="mt-auto flex flex-wrap gap-1 sm:gap-2 pl-9 sm:pl-12">
            <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
              401(k)
            </span>
            <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
              Brokerage
            </span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default PlaidConnectionOptions; 